/**
 * Remote images in HTML email are blocked until the user asks for them
 * (Gmail-style), so tracking pixels do not report when a message is opened.
 * Inline attachments (blob:/data:) and our own Supabase storage stay visible.
 */

const TRUSTED_SENDERS_KEY = "nomi.mail.trustedImageSenders";
const SHOWN_MESSAGES_KEY = "nomi.mail.shownImageMessages";
/** ponytail: cap remembered message ids; oldest drop off. Upgrade path: store by thread. */
const MAX_SHOWN_MESSAGES = 200;

const supabaseOrigin = (() => {
  try {
    return new URL(import.meta.env.VITE_SUPABASE_URL ?? "").origin;
  } catch {
    return "";
  }
})();

const isRemoteUrl = (value: string): boolean => {
  const url = value.trim().toLowerCase();
  if (!url) return false;
  if (!/^(https?:)?\/\//.test(url)) return false;
  return !(supabaseOrigin && url.startsWith(supabaseOrigin.toLowerCase()));
};

const REMOTE_IMAGE_PATTERN =
  /<img\b[^>]*\bsrc(?:set)?\s*=\s*["']?\s*(?:https?:)?\/\/|\bbackground\s*=\s*["']?\s*(?:https?:)?\/\/|url\(\s*["']?\s*(?:https?:)?\/\//i;

/** True when the email would load images from third-party servers. */
export function hasRemoteMailImages(html: string | null | undefined): boolean {
  if (!html) return false;
  return REMOTE_IMAGE_PATTERN.test(html);
}

/** Strip remote image sources from already-sanitized email body HTML. */
export function stripRemoteMailImages(bodyHtml: string): string {
  if (typeof document === "undefined") return bodyHtml;
  const template = document.createElement("template");
  template.innerHTML = bodyHtml;

  template.content.querySelectorAll("img").forEach((img) => {
    const src = img.getAttribute("src") ?? "";
    if (isRemoteUrl(src)) {
      img.removeAttribute("src");
      img.setAttribute("data-remote-blocked", "");
    }
    img.removeAttribute("srcset");
  });

  template.content.querySelectorAll("[background]").forEach((node) => {
    if (isRemoteUrl(node.getAttribute("background") ?? "")) {
      node.removeAttribute("background");
    }
  });

  return template.innerHTML;
}

/**
 * CSP for the iframe document while images are blocked: also catches CSS
 * backgrounds and anything the DOM pass missed.
 */
export function remoteImagesBlockedCspMeta(): string {
  const allowed = ["data:", "blob:", supabaseOrigin].filter(Boolean).join(" ");
  return `<meta http-equiv="Content-Security-Policy" content="img-src ${allowed}">`;
}

const normalizeSender = (email: string) => email.trim().toLowerCase();

const readTrustedSenders = (): string[] => {
  try {
    const raw = localStorage.getItem(TRUSTED_SENDERS_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? parsed.filter((value): value is string => typeof value === "string")
      : [];
  } catch {
    return [];
  }
};

export function isTrustedImageSender(email: string | null | undefined) {
  if (!email?.trim()) return false;
  return readTrustedSenders().includes(normalizeSender(email));
}

const readShownMessageIds = (): number[] => {
  try {
    const raw = localStorage.getItem(SHOWN_MESSAGES_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? parsed.filter((value): value is number => typeof value === "number")
      : [];
  } catch {
    return [];
  }
};

/** True after Show images was used on this message. */
export function isMessageImagesShown(messageId: number): boolean {
  return readShownMessageIds().includes(messageId);
}

/** Remember Show images for this message so reopening it does not ask again. */
export function rememberShownMessageImages(messageId: number): void {
  if (!Number.isFinite(messageId)) return;
  try {
    const current = readShownMessageIds().filter((id) => id !== messageId);
    localStorage.setItem(
      SHOWN_MESSAGES_KEY,
      JSON.stringify([...current, messageId].slice(-MAX_SHOWN_MESSAGES)),
    );
  } catch {
    /* ignore quota / private mode */
  }
}

export function trustImageSender(email: string): void {
  const sender = normalizeSender(email);
  if (!sender) return;
  try {
    const current = readTrustedSenders();
    if (current.includes(sender)) return;
    localStorage.setItem(
      TRUSTED_SENDERS_KEY,
      JSON.stringify([...current, sender]),
    );
  } catch {
    /* ignore quota / private mode */
  }
}
